import styles from './FieldRow.module.css';

export interface FieldRowProps {
  label: string;
  value: string | number | null | undefined;
  editable?: boolean;
  onChange?: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
}

/**
 * FieldRow - Consistent label + value display for report cards and QC workflow
 *
 * Usage:
 * - Report Card: editable=false (read-only display)
 * - QC Workflow: editable=true (form input)
 */
export function FieldRow({
  label,
  value,
  editable = false,
  onChange,
  placeholder = '',
  multiline = false,
  className = ''
}: FieldRowProps) {
  // Handle null, undefined, and string "null" (from database)
  const normalizeValue = (val: string | number | null | undefined): string => {
    if (val === null || val === undefined || val === 'null') {
      return '';
    }
    return String(val);
  };

  const displayValue = normalizeValue(value);

  if (editable) {
    return (
      <div className={`${styles.fieldRow} ${className}`}>
        <label className={styles.label}>{label}:</label>
        {multiline ? (
          <textarea
            className={styles.textarea}
            value={displayValue}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder || `Enter ${label.toLowerCase()}...`}
            rows={3}
          />
        ) : (
          <input
            type="text"
            className={styles.input}
            value={displayValue}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder || `Enter ${label.toLowerCase()}...`}
          />
        )}
      </div>
    );
  }

  // Read-only display
  return (
    <div className={`${styles.fieldRow} ${className}`}>
      <span className={styles.label}>{label}:</span>
      <span className={styles.value}>{displayValue || '[empty]'}</span>
    </div>
  );
}

export default FieldRow;
